import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  SxProps,
  Theme,
  useTheme,
  alpha,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { borderRadius, getGlassCard, getSurfaceBorderColor } from '../../../theme/theme';

interface AccordionSectionProps {
  title: string;
  expanded: boolean;
  onChange: (expanded: boolean) => void;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  summary?: React.ReactNode;
  compact?: boolean;
  sx?: SxProps<Theme>;
  summarySx?: SxProps<Theme>;
  detailsSx?: SxProps<Theme>;
}

const AccordionSection: React.FC<AccordionSectionProps> = ({
  title,
  expanded,
  onChange,
  children,
  defaultExpanded = false,
  summary,
  compact = false,
  sx,
  summarySx,
  detailsSx
}) => {
  const theme = useTheme();
  const cardStyles = React.useMemo(() => getGlassCard(theme), [theme]);
  const surfaceBorderColor = getSurfaceBorderColor(theme);
  const sectionId = title.toLowerCase().replace(/[^a-z0-9]+/g, '-');

  const handleChange = () => {
    onChange(!expanded);
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={handleChange}
      defaultExpanded={defaultExpanded}
      sx={{
        ...cardStyles,
        backgroundColor: alpha(theme.palette.background.paper, compact ? 0.14 : 0.4),
        border: `1px solid ${surfaceBorderColor}`,
        mb: compact ? 1 : 2,
        borderRadius: `${borderRadius.lg}px !important`,
        overflow: 'hidden',
        '&:before': {
          display: 'none'
        },
        '&.Mui-expanded': {
          margin: theme.spacing(0, 0, compact ? 1 : 2, 0),
          backgroundColor: alpha(theme.palette.background.paper, compact ? 0.16 : 0.5),
        },
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: surfaceBorderColor,
          backgroundColor: alpha(theme.palette.background.paper, compact ? 0.18 : 0.45),
        },
        ...sx
      }}
      aria-label={title}
    >
      <AccordionSummary
        id={`${sectionId}-header`}
        aria-controls={`${sectionId}-content`}
        expandIcon={
          <ExpandMoreIcon 
            sx={{ 
              color: compact ? theme.palette.text.secondary : theme.palette.primary.main,
              fontSize: compact ? '1.15rem' : '1.5rem',
              transition: 'transform 0.2s ease',
            }} 
          />
        }
        sx={{
          minHeight: compact ? 34 : 52,
          borderRadius: `${borderRadius.lg}px ${borderRadius.lg}px 0 0`,
          '&.Mui-expanded': {
            minHeight: compact ? 34 : 52,
            borderRadius: `${borderRadius.lg}px ${borderRadius.lg}px 0 0`,
            backgroundColor: alpha(theme.palette.primary.main, compact ? 0.018 : 0.05),
          },
          padding: compact ? theme.spacing(0, 1) : theme.spacing(0, 2),
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          '& .MuiAccordionSummary-content': {
            minWidth: 0,
            maxWidth: '100%',
            overflow: 'hidden',
            mr: compact ? 0.5 : 1,
          },
          '& .MuiAccordionSummary-expandIconWrapper': {
            flexShrink: 0,
          },
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, compact ? 0.018 : 0.03),
          },
          transition: 'background-color 0.2s ease',
          ...summarySx,
        }}
      >
        <Box
          sx={{
            width: '100%',
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: compact ? 0.75 : 1,
            overflow: 'hidden',
          }}
        >
          <Typography
            variant="subtitle1"
            component="h3"
            sx={{
              color: theme.palette.text.primary,
              fontWeight: 700,
              fontSize: compact ? '0.78rem' : '0.9375rem',
              lineHeight: 1.2,
              flex: '0 1 auto',
              minWidth: 0,
            }}
          >
            {title}
          </Typography>
          {summary && (
            <Box
              sx={{
                minWidth: 0,
                flex: '1 1 0%',
                color: theme.palette.text.secondary,
                fontSize: compact ? '0.64rem' : '0.75rem',
                lineHeight: 1.35,
                textAlign: 'right',
                '& *': {
                  fontSize: 'inherit',
                  lineHeight: 'inherit',
                  color: 'inherit',
                },
              }}
            >
              {summary}
            </Box>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails
        id={`${sectionId}-content`}
        sx={{
          backgroundColor: alpha(theme.palette.background.default, compact ? 0.08 : 0.3),
          borderRadius: `0 0 ${borderRadius.lg}px ${borderRadius.lg}px`,
          py: compact ? 0.95 : 2,
          px: compact ? 1 : 2,
          overflow: 'hidden',
          ...detailsSx,
        }}
      >
        <Box sx={{ width: '100%', overflow: 'hidden' }}>
          {children}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default AccordionSection;
